import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RRule, Options as RRuleOptions } from 'rrule';
import { DateField } from '@shared/data/entity/fields/DateField';
import { RepeatEndType } from '@shared/scenes/entity/components/fields/DateEditField/types';
import { API } from '@shared/types/api';
import { PM60, useFeatureFlag } from '@shared/util/featureFlags';
import { useIsMobile } from '@shared/util/hooks';
import { MAX_COUNT, MIN_COUNT, getAllDates, getUTCDate } from '../utils';
import { useBatchPresets } from './useBatchPresets';

// Stable empty arrays to avoid creating new references
const EMPTY_PRESETS: Partial<RRuleOptions>[] = [];
const EMPTY_DATES: Date[] = [];

interface UseBatchRuleProps {
  rruleOptions?: Partial<RRuleOptions>;
  setRruleOptions: Dispatch<SetStateAction<Partial<RRuleOptions> | undefined>>;
  field: DateField | null;
  isBatchRuleEnabled: boolean;
  userIsEditing: boolean;
  component?: API.DateComponent;
  setMinimumInSeries: Dispatch<SetStateAction<number | undefined>>;
  setTagFilter: Dispatch<SetStateAction<API.FilterByComponentFragment | undefined>>;
  recurrenceType: API.RecurrenceType | undefined;
  isWaitingForEntityBatch: boolean;
}

interface UseBatchRuleReturn {
  repeatPresets: Partial<RRuleOptions>[];
  filteredRepeatPresets: Partial<RRuleOptions>[];
  presetIndexSelected: number;
  setPresetIndexSelected: Dispatch<SetStateAction<number>>;
  isCustomRepeatSelected: boolean;
  isOffsetSelected: boolean;
  setRepeatCount: Dispatch<SetStateAction<number | undefined>>;
  repeatCount: number | undefined;
  repeatUntil: Date | undefined;
  setRepeatUntil: Dispatch<SetStateAction<Date | undefined>>;
  repeatEndType: RepeatEndType;
  setRepeatEndType: Dispatch<SetStateAction<RepeatEndType>>;
  resetBatchOptions: () => void;
}

export function useBatchRule({
  rruleOptions,
  setRruleOptions,
  field,
  isBatchRuleEnabled,
  userIsEditing,
  component,
  setMinimumInSeries,
  setTagFilter,
  recurrenceType,
  isWaitingForEntityBatch,
}: UseBatchRuleProps): UseBatchRuleReturn {
  const isMobile = useIsMobile();
  const [repeatCount, setRepeatCount] = useState<number | undefined>(
    rruleOptions?.count ?? undefined,
  );
  const [repeatUntil, setRepeatUntil] = useState<Date | undefined>(
    rruleOptions?.until ?? undefined,
  );
  const isPM60Enabled = useFeatureFlag(PM60);
  const hasDefaultAutoExtend =
    !isNil(component?.autoExtendDefault?.minimumInSeries) &&
    !isNil(component?.autoExtendDefault?.impliedFilterComponentId);
  const [repeatEndType, setRepeatEndType] = useState<RepeatEndType>(() => {
    // Initialize based on existing rruleOptions if available
    if (!isNil(rruleOptions?.count)) {
      return RepeatEndType.COUNT;
    }
    if (!isNil(rruleOptions?.until)) {
      return RepeatEndType.END_DATE;
    }
    if (!isNil(rruleOptions) && isNil(rruleOptions.count) && isNil(rruleOptions.until)) {
      return RepeatEndType.NEVER;
    }
    // Default for new schedules
    return hasDefaultAutoExtend && isPM60Enabled ? RepeatEndType.NEVER : RepeatEndType.END_DATE;
  });
  const prevRepeatEndType = useRef(repeatEndType);
  const repeatPresets = useBatchPresets({
    skip: !isBatchRuleEnabled,
    date: field?.date,
    count: repeatCount,
    until: repeatUntil,
  });
  const filteredRepeatPresets = useMemo(
    () =>
      repeatPresets.length === 0
        ? EMPTY_PRESETS
        : repeatPresets.filter(p => isMobile || p.freq === (rruleOptions?.freq ?? RRule.WEEKLY)),
    [repeatPresets, isMobile, rruleOptions?.freq],
  );
  const rruleOptionsString = useMemo(
    () => (!isNil(rruleOptions) ? new RRule(rruleOptions).toString() : ''),
    [rruleOptions],
  );

  const defaultPresetIndex = isMobile ? 1 : 0;
  const [presetIndexSelected, setPresetIndexSelected] = useState(() => {
    // If we're waiting for entity batch data to load, defer initialization
    if (isWaitingForEntityBatch) {
      return -1;
    }
    // If there's an existing rrule, try to find a matching preset
    if (!isEmpty(rruleOptionsString)) {
      return filteredRepeatPresets.findIndex(p => new RRule(p).toString() === rruleOptionsString);
    }
    // Otherwise use the default preset
    return defaultPresetIndex;
  });
  const allDates = useMemo(
    () =>
      !isNil(rruleOptions?.freq)
        ? getAllDates({
            rruleOptions: { ...rruleOptions, until: undefined, count: MAX_COUNT },
          })
        : EMPTY_DATES,
    [rruleOptions],
  );

  // Track previous rruleOptions so we can detect when the rule is first created
  const prevRruleOptionsRef = useRef<Partial<RRuleOptions> | undefined>(rruleOptions);
  // Track if we were waiting for entity batch on the previous render
  const prevIsWaitingForEntityBatch = useRef(isWaitingForEntityBatch);
  // Keep track of the previous list of occurrences so we can map the existing
  // `repeatUntil` to the same position in the refreshed list when the rule
  // changes.
  const prevAllDatesRef = useRef<Date[]>([]);

  // Update the ref every time allDates changes.
  useEffect(() => {
    prevAllDatesRef.current = allDates;
  }, [allDates]);

  // When entity batch finishes loading, update the preset index and repeatEndType based on the loaded rrule
  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      prevIsWaitingForEntityBatch.current = isWaitingForEntityBatch;
      return;
    }

    const justFinishedLoading = prevIsWaitingForEntityBatch.current && !isWaitingForEntityBatch;

    if (justFinishedLoading && !isEmpty(rruleOptionsString) && !isNil(rruleOptions)) {
      const matchIndex = filteredRepeatPresets.findIndex(
        p => new RRule(p).toString() === rruleOptionsString,
      );
      setPresetIndexSelected(matchIndex);

      // Sync repeatEndType, repeatCount, and repeatUntil with the loaded rrule options
      if (!isNil(rruleOptions.count)) {
        setRepeatCount(rruleOptions.count);
        setRepeatUntil(undefined);
        setRepeatEndType(RepeatEndType.COUNT);
      } else if (!isNil(rruleOptions.until)) {
        setRepeatUntil(rruleOptions.until);
        setRepeatCount(undefined);
        setRepeatEndType(RepeatEndType.END_DATE);
      } else {
        setRepeatCount(undefined);
        setRepeatUntil(undefined);
        setRepeatEndType(RepeatEndType.NEVER);
      }
    }

    prevIsWaitingForEntityBatch.current = isWaitingForEntityBatch;
  }, [
    isWaitingForEntityBatch,
    rruleOptionsString,
    filteredRepeatPresets,
    rruleOptions,
    isBatchRuleEnabled,
  ]);

  // Sync presetIndexSelected with rruleOptions when rruleOptions change (not just when loading finishes)
  // This handles the case where entity batch loaded before the component mounted
  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      return;
    }
    // Only sync when not actively editing and rruleOptions exist
    if (userIsEditing || isEmpty(rruleOptionsString) || isNil(rruleOptions)) {
      return;
    }

    const currentMatchIndex = filteredRepeatPresets.findIndex(
      p => new RRule(p).toString() === rruleOptionsString,
    );

    // Only update if the current presetIndexSelected doesn't match
    if (currentMatchIndex !== presetIndexSelected) {
      setPresetIndexSelected(currentMatchIndex);
    }
  }, [
    rruleOptionsString,
    filteredRepeatPresets,
    presetIndexSelected,
    userIsEditing,
    rruleOptions,
    isBatchRuleEnabled,
  ]);

  // Track if we've done initial sync of repeatEndType from rruleOptions
  const hasInitializedEndType = useRef(false);

  // Sync repeatCount, repeatUntil, and repeatEndType from rruleOptions when not actively editing
  // OR on initial load (to handle cases where rruleOptions were loaded before component mounted)
  // This ensures that when entity batch loads, the local state stays in sync
  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      return;
    }
    // Allow sync if: not editing OR this is the first time we're seeing rruleOptions with count/until
    const shouldSync = !userIsEditing || (!hasInitializedEndType.current && !isNil(rruleOptions));

    if (!shouldSync) {
      return;
    }

    // Mark as initialized at the start to prevent re-running during user edits
    if (!hasInitializedEndType.current) {
      hasInitializedEndType.current = true;
    }

    if (!isNil(rruleOptions?.count) && repeatCount !== rruleOptions.count) {
      setRepeatCount(rruleOptions.count);
      if (repeatEndType !== RepeatEndType.COUNT) {
        setRepeatEndType(RepeatEndType.COUNT);
      }
      // Update the ref so the effect below doesn't reset the count
      prevRepeatEndType.current = RepeatEndType.COUNT;
    }

    // Compare Date objects by timestamp, not reference
    const untilTimestamp = rruleOptions?.until?.getTime();
    const repeatUntilTimestamp = repeatUntil?.getTime();
    if (!isNil(untilTimestamp) && repeatUntilTimestamp !== untilTimestamp) {
      setRepeatUntil(rruleOptions?.until ?? undefined);
      if (repeatEndType !== RepeatEndType.END_DATE) {
        setRepeatEndType(RepeatEndType.END_DATE);
      }
      // Update the ref so the effect below doesn't reset the until date
      prevRepeatEndType.current = RepeatEndType.END_DATE;
    }

    // If rruleOptions has neither count nor until, and we're not in NEVER mode
    if (
      !isNil(rruleOptions) &&
      isNil(rruleOptions.count) &&
      isNil(rruleOptions.until) &&
      repeatEndType !== RepeatEndType.NEVER
    ) {
      setRepeatEndType(RepeatEndType.NEVER);
      // Update the ref so the effect below doesn't reset
      prevRepeatEndType.current = RepeatEndType.NEVER;
    }
  }, [rruleOptions, repeatCount, repeatUntil, repeatEndType, userIsEditing, isBatchRuleEnabled]);

  // set initial until
  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      return;
    }
    if (isEmpty(allDates) || userIsEditing) {
      return;
    }
    setRepeatUntil(prev => {
      if (!isNil(rruleOptions?.until) || repeatEndType !== RepeatEndType.END_DATE) {
        return prev;
      }
      const lastDate = allDates[allDates.length - 1];
      // Only set if prev is undefined or dates are different (comparing by date only, not time)
      // This prevents a cycle with the dtstart time sync effect
      if (!isNil(prev)) {
        const prevDateOnly = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate());
        const lastDateOnly = new Date(
          lastDate.getFullYear(),
          lastDate.getMonth(),
          lastDate.getDate(),
        );
        if (prevDateOnly.getTime() === lastDateOnly.getTime()) {
          return prev;
        }
      }
      return lastDate;
    });
  }, [rruleOptions?.until, repeatEndType, allDates, userIsEditing, isBatchRuleEnabled]);

  /*
    When a brand-new rrule is created (it was previously undefined), default the rule
    to end after MAX_COUNT occurrences by setting the `until` option to the date of
    the 100th occurrence (MAX_COUNT is 100). This should *only* happen on initial
    creation – users can subsequently clear or adjust the `until` value and we will
    not overwrite it.
  */
  useEffect(() => {
    // only run when batch rule feature is enabled and user is actively editing
    if (!isBatchRuleEnabled || !userIsEditing) {
      return;
    }

    const wasPreviouslyUndefined = isNil(prevRruleOptionsRef.current);
    const isNowDefined = !isNil(rruleOptions);

    if (wasPreviouslyUndefined && isNowDefined && hasDefaultAutoExtend) {
      setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? MAX_COUNT);
      setTagFilter({
        componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
    }

    // update ref for next render
    prevRruleOptionsRef.current = rruleOptions;
  }, [
    rruleOptions,
    isBatchRuleEnabled,
    userIsEditing,
    setRruleOptions,
    component,
    setMinimumInSeries,
    setTagFilter,
    hasDefaultAutoExtend,
  ]);

  /*
    When the repeat end type changes, we need to update the repeat count and until date.
    We only auto-adjust `repeatUntil` when:
      1. The series (`allDates`) itself changes, **and**
      2. The user-selected `repeatUntil` is **undefined** OR it lies *after* the last
         valid occurrence (i.e., is out of range).
    Otherwise, we preserve the user's explicit selection.
  */
  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      return;
    }
    if (isEmpty(allDates) || !userIsEditing) {
      return;
    }

    // Only recalculate `repeatUntil` automatically when the generated series
    // (allDates) itself has changed. This avoids clobbering a date that the
    // user explicitly picked.
    const allDatesChanged = () => {
      if (prevAllDatesRef.current.length !== allDates.length) {
        return true;
      }
      for (let i = 0; i < allDates.length; i += 1) {
        if (allDates[i].getTime() !== prevAllDatesRef.current[i].getTime()) {
          return true;
        }
      }
      return false;
    };

    const lastDateInSeries = allDates[allDates.length - 1];

    const recurrenceDefinitionChanged =
      allDatesChanged() || repeatEndType !== prevRepeatEndType.current;

    if (!recurrenceDefinitionChanged) {
      // Likely a direct user edit to until/count; leave untouched.
      return;
    }

    if (repeatEndType === RepeatEndType.END_DATE) {
      // Ensure count is cleared whenever we're driving via an explicit end date
      setRepeatCount(undefined);

      let candidateUntil: Date = lastDateInSeries;

      if (!isEmpty(allDates)) {
        // Determine if we need to auto-adjust. We do so only when repeatUntil is
        // undefined *or* after the last valid date (out of range).
        const shouldAutoAdjust =
          isNil(repeatUntil) || repeatUntil.getTime() > lastDateInSeries.getTime();

        if (!shouldAutoAdjust) {
          // User-selected date is within bounds – leave it as-is.
          prevRepeatEndType.current = repeatEndType;
          return;
        }

        // From here on, we know we must adjust because repeatUntil is undefined
        // or invalid.

        if (!isNil(repeatUntil)) {
          // First try to find an exact match (rare but fast).
          const exactIdx = allDates.findIndex(d => d.getTime() === repeatUntil.getTime());
          if (exactIdx !== -1) {
            candidateUntil = allDates[exactIdx];
          } else {
            // Map same index if possible.
            const prevIdx = prevAllDatesRef.current.findIndex(
              d => d.getTime() === repeatUntil.getTime(),
            );
            if (prevIdx !== -1 && prevIdx < allDates.length) {
              candidateUntil = allDates[prevIdx];
            }
            // else keep candidateUntil as lastDateInSeries.
          }
        }
      }

      // Only update state if the candidate date is actually different – this
      // prevents unnecessary renders and potential update loops.
      if (isNil(repeatUntil) || candidateUntil.getTime() !== repeatUntil.getTime()) {
        setRepeatUntil(candidateUntil);
      }
    } else if (repeatEndType === RepeatEndType.COUNT) {
      setRepeatUntil(undefined);
      setRepeatCount(allDates.length);
    } else if (repeatEndType === RepeatEndType.NEVER) {
      setRepeatUntil(undefined);
      setRepeatCount(undefined);
    }
    prevRepeatEndType.current = repeatEndType;
  }, [repeatEndType, allDates, repeatUntil, userIsEditing, isBatchRuleEnabled]);

  /*
    When the repeat count changes, we need to update the rule and until date.
  */
  useEffect(() => {
    if (!isBatchRuleEnabled || !userIsEditing || isNil(repeatCount) || isWaitingForEntityBatch) {
      return;
    }
    setRruleOptions(prev => {
      if (
        !isNil(prev) &&
        prev?.count !== repeatCount &&
        repeatCount >= MIN_COUNT &&
        repeatCount <= MAX_COUNT
      ) {
        setRepeatUntil(undefined);
        return { ...prev, until: undefined, count: repeatCount };
      }
      return prev;
    });
  }, [isBatchRuleEnabled, repeatCount, setRruleOptions, userIsEditing, isWaitingForEntityBatch]);

  /*
    When the repeat until date changes, we need to update the rule and count.
  */
  useEffect(() => {
    if (!isBatchRuleEnabled || !userIsEditing || isNil(repeatUntil) || isWaitingForEntityBatch) {
      return;
    }
    setRruleOptions(prev => {
      // Compare Date objects by timestamp, not reference
      const prevUntilTimestamp = prev?.until?.getTime();
      const repeatUntilTimestamp = repeatUntil?.getTime();
      if (!isNil(prev) && prevUntilTimestamp !== repeatUntilTimestamp) {
        setRepeatCount(undefined);
        return { ...prev, until: repeatUntil, count: undefined };
      }
      return prev;
    });
  }, [isBatchRuleEnabled, repeatUntil, setRruleOptions, userIsEditing, isWaitingForEntityBatch]);

  /*
    When the repeat count and until date are both undefined, we need to update the rule.
  */
  useEffect(() => {
    if (
      !isBatchRuleEnabled ||
      !userIsEditing ||
      !isNil(repeatCount) ||
      !isNil(repeatUntil) ||
      isWaitingForEntityBatch
    ) {
      return;
    }
    setRruleOptions(prev => {
      if (!isNil(prev) && (prev.until !== undefined || prev.count !== undefined)) {
        return { ...prev, until: undefined, count: undefined };
      }
      return prev;
    });
  }, [
    repeatCount,
    repeatUntil,
    isBatchRuleEnabled,
    setRruleOptions,
    userIsEditing,
    isWaitingForEntityBatch,
  ]);

  /*
    When the field date changes, we need to update the dtstart.
  */
  useEffect(() => {
    if (isNil(field?.date) || !isBatchRuleEnabled || !userIsEditing || isWaitingForEntityBatch) {
      return;
    }
    setRruleOptions(prev => {
      const dateObj = new Date(field.date);
      const dstartFormatted = getUTCDate(dateObj);
      if (!isNil(prev) && !isNil(dstartFormatted) && prev.dtstart !== dstartFormatted) {
        return { ...prev, dtstart: dstartFormatted };
      }
      return prev;
    });
  }, [
    isBatchRuleEnabled,
    field,
    setRruleOptions,
    userIsEditing,
    repeatUntil,
    isWaitingForEntityBatch,
  ]);

  /*
    When the dtstart changes, we need to update the until time.
  */
  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      return;
    }
    if (isNil(repeatUntil) || isNil(rruleOptions?.dtstart)) {
      return;
    }
    setRepeatUntil(prev => {
      const dtstartDate = moment(rruleOptions?.dtstart);
      const dtstartHour = dtstartDate.hour();
      const dtstartMinute = dtstartDate.minute();
      const dtstartSecond = dtstartDate.second();
      const dtstartMillisecond = dtstartDate.millisecond();

      const untilDate = moment(repeatUntil);
      if (
        dtstartHour === untilDate.hour() &&
        dtstartMinute === untilDate.minute() &&
        dtstartSecond === untilDate.second() &&
        dtstartMillisecond === untilDate.millisecond()
      ) {
        return prev;
      }

      untilDate.hour(dtstartHour);
      untilDate.minute(dtstartMinute);
      untilDate.second(dtstartSecond);
      untilDate.millisecond(dtstartMillisecond);

      return untilDate.toDate();
    });
  }, [repeatUntil, setRepeatUntil, rruleOptions?.dtstart, isBatchRuleEnabled]);

  /*
    When the preset index changes, we need to update the rule.
    Skip this effect if we're waiting for entity batch to prevent overriding custom rules.
  */
  useEffect(() => {
    if (
      !isBatchRuleEnabled ||
      isNil(rruleOptions?.dtstart) ||
      isNil(filteredRepeatPresets[0]?.dtstart) ||
      rruleOptions?.dtstart.toISOString() === filteredRepeatPresets[0].dtstart.toISOString() ||
      presetIndexSelected === -1 ||
      !userIsEditing ||
      isWaitingForEntityBatch
    ) {
      return;
    }
    setRruleOptions(filteredRepeatPresets[presetIndexSelected]);
  }, [
    rruleOptions?.dtstart,
    setRruleOptions,
    filteredRepeatPresets,
    presetIndexSelected,
    isBatchRuleEnabled,
    userIsEditing,
    isWaitingForEntityBatch,
  ]);

  const isOffsetSelected = recurrenceType === 'OFFSET';
  const isCustomRepeatSelected = recurrenceType === 'RRULE_SCHEDULE' && presetIndexSelected === -1;

  const resetBatchOptions = useCallback(() => {
    setRepeatCount(MAX_COUNT);
    setRepeatUntil(undefined);
    setRepeatEndType(hasDefaultAutoExtend ? RepeatEndType.NEVER : RepeatEndType.END_DATE);
    setPresetIndexSelected(defaultPresetIndex);
    setRruleOptions(undefined);
  }, [
    setRepeatCount,
    setRepeatUntil,
    setRepeatEndType,
    setPresetIndexSelected,
    setRruleOptions,
    defaultPresetIndex,
    hasDefaultAutoExtend,
  ]);

  return {
    repeatPresets,
    filteredRepeatPresets,
    setRepeatCount,
    repeatCount,
    presetIndexSelected,
    setPresetIndexSelected,
    isCustomRepeatSelected,
    isOffsetSelected,
    repeatUntil,
    setRepeatUntil,
    repeatEndType,
    setRepeatEndType,
    resetBatchOptions,
  };
}
